import React, {useEffect} from "react"
import CustomModal, {ModalPosition} from "./customModal";
import Button from "../button/button";
import {ButtonStatus} from "../button/enum";
import {differ} from "../../util/util";

export enum NotificationType {
    success,
    error,
    info,
}

interface IProps {
    isOpen: boolean,
    type: NotificationType,
    position?: ModalPosition,
    autoDismiss?: boolean,
    title: string,
    onClose: () => void,
}

export default function Notification(props: IProps) {
    const {isOpen, type, position, autoDismiss = true, title, onClose} = props

    useEffect(() => {
        if (isOpen && autoDismiss) {
            const duration = title.length <= 50 ? 3000 : (Math.floor((title.length / 50 * 30)) * 100)
            const timer = differ(() => {
                onClose && onClose()
            }, duration)
            return () => {
                timer && clearTimeout(timer)
            }
        }
    }, [isOpen])

    const $successIcon = (
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_f_186_1808)">
                <circle cx="12.5" cy="12" r="9.5" fill="#219653" stroke="#219653"/>
                <path d="M11.7257 15.4509C11.5205 15.4509 11.3153 15.373 11.1586 15.2164L8.98496 13.0427C8.67168 12.7294 8.67168 12.2219 8.98496 11.9096C9.29823 11.5963 9.80478 11.5954 10.1181 11.9086L11.7257 13.5162L15.5069 9.73496C15.8202 9.42168 16.3268 9.42168 16.64 9.73496C16.9533 10.0482 16.9533 10.5557 16.64 10.869L12.2927 15.2164C12.136 15.373 11.9308 15.4509 11.7257 15.4509Z" fill="white"/>
            </g>
            <circle cx="12.5" cy="12" r="9.5" fill="#219653" stroke="#219653"/>
            <path d="M11.7257 15.4509C11.5205 15.4509 11.3153 15.373 11.1586 15.2164L8.98496 13.0427C8.67168 12.7294 8.67168 12.2219 8.98496 11.9096C9.29823 11.5963 9.80478 11.5954 10.1181 11.9086L11.7257 13.5162L15.5069 9.73496C15.8202 9.42168 16.3268 9.42168 16.64 9.73496C16.9533 10.0482 16.9533 10.5557 16.64 10.869L12.2927 15.2164C12.136 15.373 11.9308 15.4509 11.7257 15.4509Z" fill="white"/>
            <defs>
                <filter id="filter0_f_186_1808" x="0.5" y="0" width="24" height="24" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur_186_1808"/>
                </filter>
            </defs>
        </svg>
    )

    const $errIcon = (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 11.9998C22 17.5238 17.523 21.9998 12 21.9998C6.477 21.9998 2 17.5238 2 11.9998C2 6.47782 6.477 1.99982 12 1.99982C17.523 1.99982 22 6.47782 22 11.9998Z" fill="#C54644"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M12.8701 12.6307C12.8701 13.1127 12.4771 13.5057 11.9951 13.5057C11.5131 13.5057 11.1201 13.1127 11.1201 12.6307V8.21069C11.1201 7.72869 11.5131 7.33569 11.9951 7.33569C12.4771 7.33569 12.8701 7.72869 12.8701 8.21069V12.6307ZM11.1251 15.8035C11.1251 15.3215 11.5161 14.9285 11.9951 14.9285C12.4881 14.9285 12.8801 15.3215 12.8801 15.8035C12.8801 16.2855 12.4881 16.6785 12.0051 16.6785C11.5201 16.6785 11.1251 16.2855 11.1251 15.8035Z" fill="white"/>
        </svg>
    )

    const $infoIcon = (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Iconly/Bulk/Info Circle">
                <path id="Fill 1" opacity="0.4" d="M22 12C22 17.524 17.523 22 12 22C6.477 22 2 17.524 2 12C2 6.478 6.477 2 12 2C17.523 2 22 6.478 22 12Z" fill="#F2994A"/>
                <path id="Combined Shape" fillRule="evenodd" clipRule="evenodd" d="M12.8701 12.6309C12.8701 13.1129 12.4771 13.5059 11.9951 13.5059C11.5131 13.5059 11.1201 13.1129 11.1201 12.6309V8.21094C11.1201 7.72894 11.5131 7.33594 11.9951 7.33594C12.4771 7.33594 12.8701 7.72894 12.8701 8.21094V12.6309ZM11.1251 15.8037C11.1251 15.3217 11.5161 14.9287 11.9951 14.9287C12.4881 14.9287 12.8801 15.3217 12.8801 15.8037C12.8801 16.2857 12.4881 16.6787 12.0051 16.6787C11.5201 16.6787 11.1251 16.2857 11.1251 15.8037Z" fill="#F2994A"/>
            </g>
        </svg>
    )

    const $closeIcon = (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 4.5L14.0806 14.0806" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4.5 14.0806L14.0806 4.49998" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )

    const renderIcon = () => {
        switch (type) {
        case NotificationType.success:
            return $successIcon
        case NotificationType.error:
            return $errIcon
        case NotificationType.info:
            return $infoIcon
        default:
            return null
        }
    }

    return (
        <CustomModal isOpen={isOpen} position={position} containerClassName={"p-2.5"}>
            <div className={"flex flex-row items-center justify-between max-w-md"}>
                <div className={"px-2 flex flex-row items-center"}>
                    {renderIcon()}
                </div>

                <p className={"text-black test-lg ml-2"}>{title.length > 100 ? `${title.slice(0, 100)}...` : title}</p>

                <Button status={ButtonStatus.float} onClick={() => {
                    onClose && onClose()
                }}>
                    {$closeIcon}
                </Button>
            </div>
        </CustomModal>
    )
}
